import {
  dashboardStatCardClassName,
  dashboardStatCardContentClassName,
} from "@/modules/dashboard/presentation/ui/dashboard-card-styles";
import { Card, CardContent, cn, Skeleton } from "@devhop/ui";
import type { LucideIcon } from "lucide-react";
import type { ReactNode } from "react";

export type DashboardStatTone =
  | "revenue"
  | "attendance"
  | "students"
  | "packages"
  | "refunds";

type DashboardStatCardProps = {
  title: string;
  value: ReactNode;
  isLoading: boolean;
  icon: LucideIcon;
  tone?: DashboardStatTone;
  onClick?: () => void;
  className?: string;
};

const toneStyles = {
  revenue: {
    icon: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400",
    hover: "hover:border-emerald-500/30 hover:bg-emerald-500/5",
  },
  attendance: {
    icon: "bg-sky-500/10 text-sky-700 dark:text-sky-400",
    hover: "hover:border-sky-500/30 hover:bg-sky-500/5",
  },
  students: {
    icon: "bg-violet-500/10 text-violet-700 dark:text-violet-400",
    hover: "hover:border-violet-500/30 hover:bg-violet-500/5",
  },
  packages: {
    icon: "bg-amber-500/10 text-amber-700 dark:text-amber-400",
    hover: "hover:border-amber-500/30 hover:bg-amber-500/5",
  },
  refunds: {
    icon: "bg-orange-500/10 text-orange-700 dark:text-orange-400",
    hover: "hover:border-orange-500/30 hover:bg-orange-500/5",
  },
} as const;

export function DashboardStatCard({
  title,
  value,
  isLoading,
  icon: Icon,
  tone = "revenue",
  onClick,
  className,
}: DashboardStatCardProps) {
  const styles = toneStyles[tone];
  const interactive = !!onClick;

  return (
    <Card
      className={cn(
        dashboardStatCardClassName,
        "w-full self-start transition-colors",
        interactive && "cursor-pointer",
        interactive && styles.hover,
        className,
      )}
      onClick={onClick}
      onKeyDown={
        interactive
          ? (e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                onClick?.();
              }
            }
          : undefined
      }
      role={interactive ? "button" : undefined}
      tabIndex={interactive ? 0 : undefined}
    >
      <CardContent className={dashboardStatCardContentClassName}>
        <div className="min-w-0 space-y-2">
          <p className="text-muted-foreground text-sm">{title}</p>
          {isLoading ? (
            <Skeleton className="h-9 w-32 max-w-full" />
          ) : (
            <div className="font-semibold text-3xl tabular-nums tracking-tight">
              {value}
            </div>
          )}
        </div>
        <div
          className={cn(
            "flex size-11 shrink-0 items-center justify-center rounded-xl",
            styles.icon,
          )}
        >
          <Icon className="size-5" aria-hidden />
        </div>
      </CardContent>
    </Card>
  );
}
